import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { NextStepsService } from '@features/next-steps/services/next-steps.service';
import { nextStepContexts } from '@shared/constants/next-step-contexts';
import { NextStep } from '@shared/models/next-step';
import { NextStepFilters } from '@shared/models/next-step-filters';
import { defaultPaginationState, PaginationState } from '@shared/models/pagination-state';
import { Sorting } from '@shared/models/sorting';
import { DrawerModalService } from '@shared/services/drawer-modal.service';
import { SessionService } from '@shared/services/session.service';
import { LoadingState } from '@shared/types/loading-state';
import { map, Observable, take } from 'rxjs';
import { SearchOptionGroup } from '@shared/models/search-option-group';
import { regexList } from '@shared/constants/regex-list';

@Component({
  selector: 'app-next-steps-container',
  templateUrl: './next-steps-container.component.html',
  styleUrl: './next-steps-container.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NextStepsContainerComponent implements OnInit {
  @Input() preview = true;
  nextSteps$: Observable<NextStep[]>;
  loadingState$: Observable<LoadingState> = this.nextStepsService.getNextStepsLoading();
  loadingText$: Observable<string | undefined> = this.nextStepsService.getNextStepsLoadingText();
  paginationState$: Observable<PaginationState | undefined> = this.nextStepsService.getNextStepsPaginationState();
  filters: NextStepFilters;
  sorting: Sorting = { key: 'due_date', direction: 'asc' };
  nextStepContexts = nextStepContexts;
  searchOptionGroups: SearchOptionGroup[] = [
    {
      key: 'next_step_filters',
      title: 'Next Step Filters',
      searchOptions: [
        {
          key: 'lead_id',
          title: 'Lead #',
          type: 'number',
          placeholder: '12345',
          filter: regexList.only_number,
        },
        {
          key: 'opportunity_id',
          title: 'Opportunity #',
          type: 'number',
          placeholder: '12345',
          filter: regexList.only_number,
        },
        {
          key: 'due_date',
          title: 'Due Date',
          type: 'dateRange',
          filter: regexList.no_match_when_typing,
        },
        {
          key: 'group_type',
          title: 'Group Type',
          type: 'string',
          suggestedValues: [
            { value: 'partner', title: 'Partner' },
            { value: 'customer', title: 'Customer' },
          ],
          filter: regexList.no_match_when_typing,
        },
        {
          key: 'next_step_context',
          title: 'Context',
          type: 'string',
          suggestedValues: nextStepContexts.map((context) => ({
            value: context.reference,
            title: context.title,
          })),
          filter: regexList.no_match_when_typing,
        },
      ],
    },
  ];

  constructor(
    private nextStepsService: NextStepsService,
    private sessionService: SessionService,
    private drawerModalService: DrawerModalService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.sessionService
      .getSession()
      .pipe(take(1))
      .subscribe((session) => {
        this.filters = { opportunity_owner_or_lead_owner_or_account_manager_id: session?.id };
        this.loadNextSteps();
        this.changeDetectorRef.markForCheck();
      });

    this.nextStepsService
      .getNextStepsFilters()
      .pipe(take(1))
      .subscribe((filters) => {
        if (!!filters) {
          this.filters = filters || {};
          this.searchOptionGroups = this.searchOptionGroups.map((searchOptionGroup) => {
            return {
              ...searchOptionGroup,
              searchOptions: searchOptionGroup.searchOptions.map((searchOption) => {
                return {
                  ...searchOption,
                  value: filters[searchOption.key as keyof NextStepFilters],
                };
              }),
            };
          });
          this.changeDetectorRef.markForCheck();
        }
      });
    this.nextSteps$ = this.nextStepsService.getSessionNextSteps().pipe(
      map((nextSteps) => {
        return this.preview ? nextSteps.slice(0, 5) : nextSteps;
      }),
    );
  }

  loadNextSteps(paginationState: PaginationState = defaultPaginationState) {
    this.nextStepsService.loadNextSteps(this.filters, paginationState, this.sorting);
  }

  updateFilters(filters: any) {
    const nextStepsFilter: NextStepFilters = {
      lead_id: filters.lead_id ? Number(filters.lead_id) : undefined,
      opportunity_id: filters.opportunity_id ? Number(filters.opportunity_id) : undefined,
      from_due_date: filters.due_date?.min,
      to_due_date: filters.due_date?.max,
      group_type: filters.group_type,
      next_step_context: filters.next_step_context,
    };
    this.nextStepsService.setNextStepsFilters(nextStepsFilter);
  }

  presentNextStepsModal() {
    this.drawerModalService.presentModal(NextStepsContainerComponent, { preview: false }, 'md', true);
  }

  closeModal() {
    this.drawerModalService.dismissModal();
  }
}
